import {
  PublicClientApplication,
  type Configuration,
  type AccountInfo,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
} from '@azure/msal-browser';
import { apiScope, config } from '../config';

const msalConfig: Configuration = {
  auth: {
    clientId: config.entraClientId || '00000000-0000-0000-0000-000000000000',
    authority: `https://login.microsoftonline.com/${config.entraTenantId || 'common'}`,
    redirectUri: config.redirectUri,
    postLogoutRedirectUri: config.redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
};

function needsInteractiveLogin(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  // Silent iframe renewal can time out (CSP, third-party cookies, idle session).
  // Fall back to a full redirect instead of surfacing a dead maps page.
  return (
    err instanceof BrowserAuthError &&
    err.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout
  );
}

export const msalInstance = new PublicClientApplication(msalConfig);

let initPromise: Promise<void> | null = null;

function ensureActiveAccount(): void {
  if (msalInstance.getActiveAccount()) return;
  const account = msalInstance.getAllAccounts()[0];
  if (account) {
    msalInstance.setActiveAccount(account);
  }
}

export function ensureMsalInitialized(): Promise<void> {
  if (!initPromise) {
    initPromise = msalInstance.initialize().then(async () => {
      const result = await msalInstance.handleRedirectPromise();
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else {
        ensureActiveAccount();
      }
    });
  }
  return initPromise;
}

export async function getAccessToken(account: AccountInfo): Promise<string> {
  const request = {
    account,
    scopes: [apiScope],
  };
  try {
    const result = await msalInstance.acquireTokenSilent(request);
    return result.accessToken;
  } catch (err) {
    if (needsInteractiveLogin(err)) {
      await msalInstance.acquireTokenRedirect(request);
      return '';
    }
    throw err;
  }
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}
